import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { BandeauAlerte, BandeauSignature, Embleme, IconeCoche } from '../../components/index.js';
import { useChargement, type Chargement } from '../../hooks/useChargement.js';
import { useOrganisateur } from '../../hooks/useOrganisateur.js';
import { api, capitale, dateLongue, type DetailEvenement } from '../../lib/api.js';

export interface ContexteEvenement {
  detail: DetailEvenement;
  recharger: () => Promise<void>;
}

const ETAPES_NAV: { chemin: string; label: string }[] = [
  { chemin: 'evenement', label: 'Événement' },
  { chemin: 'participants', label: 'Participants' },
  { chemin: 'hotes', label: 'Hôtes par étape' },
  { chemin: 'repartition', label: 'Répartition' },
  { chemin: 'planning', label: 'Planning' },
  { chemin: 'diffusion', label: 'Diffusion' },
];

/** Étapes « faites » : affichées avec une coche dans la barre latérale (cf. captures 4 et 5). */
function etapesFaites(d: DetailEvenement): Set<string> {
  const faites = new Set<string>(['evenement']);
  if (d.inscriptions.participants > 0) faites.add('participants');
  if (d.planning.repartitionGenereeAt) {
    faites.add('hotes');
    faites.add('repartition');
  }
  return faites;
}

/** Gabarit organisateur : barre latérale à 6 étapes + contenu. */
export function EvenementLayout() {
  const organisateur = useOrganisateur();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const chargement: Chargement<DetailEvenement> = useChargement(() => api.get<DetailEvenement>(`/api/events/${eventId}`), [eventId, organisateur?.id]);
  const { donnees, erreur } = chargement;

  const deconnexion = async () => {
    await api.post('/api/auth/logout', {});
    navigate('/connexion');
  };

  return (
    <div className="app-shell">
      <aside className="barre-laterale">
        <Link to="/app" className="logo-barre">
          <span className="nameplate">
            <Embleme />
            <span className="wordmark">Lions Clubs</span>
          </span>
          <BandeauSignature petit />
          <span className="display nom-evenement">{donnees?.event.nom ?? 'Dîner tournant'}</span>
          {donnees ? <span className="texte-muet date-evenement">{capitale(dateLongue(donnees.event.date))}</span> : null}
        </Link>
        <nav aria-label="Étapes de préparation">
          <ol className="etapes-nav">
            {ETAPES_NAV.map((e, i) => {
              const faite = donnees ? etapesFaites(donnees).has(e.chemin) : false;
              return (
                <li key={e.chemin}>
                  <NavLink to={`/app/e/${eventId}/${e.chemin}`} className={({ isActive }) => `etape-lien${isActive ? ' active' : ''}${faite ? ' faite' : ''}`}>
                    <span className="num" aria-hidden="true">
                      {faite ? <IconeCoche taille={14} /> : i + 1}
                    </span>
                    <span>{e.label}</span>
                    {faite ? <span className="sr-only"> (fait)</span> : null}
                  </NavLink>
                </li>
              );
            })}
          </ol>
        </nav>
        <div className="pied-barre">
          <Link to="/app" className="texte-muet">
            ← Mes événements
          </Link>
          <button type="button" className="btn" onClick={deconnexion}>
            Se déconnecter
          </button>
        </div>
      </aside>
      <main className="contenu-app">
        {erreur ? (
          <BandeauAlerte niveau="attention" role="alert" titre={erreur}>
            <Link to="/app">Retour à mes événements</Link>
          </BandeauAlerte>
        ) : null}
        {donnees ? <Outlet context={{ detail: donnees, recharger: chargement.recharger } satisfies ContexteEvenement} /> : !erreur ? <p className="texte-muet">Chargement…</p> : null}
      </main>
    </div>
  );
}
